/**
 * BottomTabNavigation Component
 * 
 * Provides a mobile-friendly bottom navigation bar with icons and labels.
 * This component can be toggled on/off via the admin settings.
 * 
 * Features:
 * - Icons above text labels for each tab
 * - Fixed positioning at bottom of screen
 * - Responsive design for different screen sizes
 * - Maintains existing tab functionality using Radix UI
 */

import React from 'react';
import { CheckSquare, Gift, Trophy, Settings, Wallet, Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TabSettings } from '@/utils/storage';

interface BottomTabNavigationProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  editMode: boolean;
  walletEnabled: boolean;
  tabSettings?: TabSettings;
}

const BottomTabNavigation: React.FC<BottomTabNavigationProps> = ({
  activeTab,
  onTabChange,
  editMode,
  walletEnabled,
  tabSettings
}) => {
  // Get effective tab settings with fallbacks
  const effectiveTabSettings = tabSettings || {
    tasksEnabled: true,
    rewardsEnabled: true,
    trophiesEnabled: true,
    wishListEnabled: false,
    walletEnabled: true
  };

  const tabs = [
    {
      id: 'tasks',
      label: 'Tasks',
      icon: CheckSquare,
      enabled: effectiveTabSettings.tasksEnabled
    },
    {
      id: 'rewards',
      label: 'Rewards',
      icon: Gift,
      enabled: effectiveTabSettings.rewardsEnabled
    },
    {
      id: 'wallet',
      label: 'Wallet',
      icon: Wallet,
      enabled: walletEnabled && effectiveTabSettings.walletEnabled
    },
    {
      id: 'wishlist',
      label: 'Wish List',
      icon: Heart,
      enabled: effectiveTabSettings.wishListEnabled
    },
    {
      id: 'trophies',
      label: 'Trophies',
      icon: Trophy,
      enabled: effectiveTabSettings.trophiesEnabled
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: Settings,
      enabled: editMode
    }
  ].filter(tab => tab.enabled);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border iphone-safe-bottom-nav">
      <div className="flex items-center justify-around px-1 py-2 max-w-3xl mx-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={cn(
                "flex flex-col items-center justify-center py-2 px-1 rounded-lg transition-all duration-200",
                "min-w-0 flex-1 min-h-[60px]", // Increased min-height for better text spacing
                isActive 
                  ? "text-primary bg-primary/10" 
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              <Icon 
                size={20} // Slightly smaller icon to make room for text
                className={cn(
                  "mb-1 transition-transform duration-200 flex-shrink-0",
                  isActive && "scale-110"
                )}
              />
              <span className={cn(
                "text-xs font-medium leading-tight text-center max-w-full break-words", // Removed truncate, added break-words
                "px-1 whitespace-normal", // Allow text wrapping instead of truncation
                isActive && "font-semibold"
              )}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BottomTabNavigation;